import React from "react";
import { Redirect } from "react-router-dom";
import { Error404 } from "ui-kit-ck-consultant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUsersCog,
  faCogs,
  faUser,
  faTools,
  faStoreAlt,
  faChartBar,
  faTable,
  faWindowClose,
  faChartLine,
  faSnowflake,
  faCaravan,
  faBolt,
  faSearch,
  faExclamationCircle,
  faBriefcase,
  faGlobe,
  faFileAlt,
  faCloudShowersHeavy,
  faFire,
} from "@fortawesome/free-solid-svg-icons";

import AuthLayout from "../layouts/Auth";
import AuthV2Layout from "../layouts/AuthV2";
import DashboardLayout from "../layouts/Dashboard";

import Signin from "../views/auth/Signin";
import Forgot from "../views/auth/Forgot";
import Signout from "../views/auth/Signout";
import Home from "../views/dashboard/Home";
import Settings from "../views/dashboard/Settings";
import Documents from "../views/dashboard/Documents";
import DocumentEditor from "../views/dashboard/DocumentEditor";
import AdminGeneral from "../views/dashboard/admin/AdminGeneral";
import AdminUser from "../views/dashboard/admin/AdminUser";
import AdminGarage from "../views/dashboard/admin/AdminGarage";
import AdminAgency from "../views/dashboard/admin/AdminAgency";
import BrokenGlass from "../views/dashboard/statistics/BrokenGlass";
import StatisticsGeneral from "../views/dashboard/statistics/StatisticsGeneral";
import StatisticsAbeille from "../views/dashboard/statistics/StatisticsAbeille";
import StatisticsAllianz from "../views/dashboard/statistics/StatisticsAllianz";
import StatisticsAllianzOM from "../views/dashboard/statistics/StatisticsAllianzOM";
import StatisticsAllianzPJ from "../views/dashboard/statistics/StatisticsAllianzPJ";
import StatisticsAxa from "../views/dashboard/statistics/StatisticsAxa";
import Selfcare from "../views/dashboard/statistics/Selfcare";
import Ega from "../views/dashboard/statistics/EGA";
import Caravaning from "../views/dashboard/statistics/Caravaning";
import Electric from "../views/dashboard/statistics/Electric";
import Hail from "../views/dashboard/statistics/Hail";
import Fire from "../views/dashboard/statistics/Fire";
import Ranking from "../views/dashboard/statistics/Ranking";
import StatisticsCovea from "../views/dashboard/statistics/StatisticsCovea";
import StatisticsHelvetia from "../views/dashboard/statistics/StatisticsHelvetia";
import ReparatorPerformance from "../views/dashboard/statistics/ReparatorPerformance";
import VVR from "../views/dashboard/statistics/VVR";
import VVNR from "../views/dashboard/statistics/VVNR";
import IRSA from "../views/dashboard/statistics/IRSA";
import ProjectE from "../views/dashboard/statistics/ProjectE";
import Audit from "../views/dashboard/Audit";
import Anomaly from "../views/dashboard/Anomaly";
import MissionsTable from "../views/dashboard/missions/MissionsTable";
import MissionsMap from "../views/dashboard/missions/MissionsMap";

import RIGHTS from "./rights.json";

// Icons are built ONCE, at module scope. Do not inline <FontAwesomeIcon/> into a
// descriptor built during a render: the ui-kit Sidebar JSON.stringify-diffs its
// `views` prop, and a React element created inside render() carries a circular
// _owner fiber -> "Converting circular structure to JSON" -> white screen.
const ICON_HOME = <FontAwesomeIcon icon={faHome} />;
const ICON_ADMIN = <FontAwesomeIcon icon={faUsersCog} />;
const ICON_COGS = <FontAwesomeIcon icon={faCogs} />;
const ICON_USER = <FontAwesomeIcon icon={faUser} />;
const ICON_TOOLS = <FontAwesomeIcon icon={faTools} />;
const ICON_STORE = <FontAwesomeIcon icon={faStoreAlt} />;
const ICON_CHART_BAR = <FontAwesomeIcon icon={faChartBar} />;
const ICON_TABLE = <FontAwesomeIcon icon={faTable} />;
const ICON_GLASS = <FontAwesomeIcon icon={faWindowClose} />;
const ICON_CHART_LINE = <FontAwesomeIcon icon={faChartLine} />;
const ICON_SNOWFLAKE = <FontAwesomeIcon icon={faSnowflake} />;
const ICON_CARAVAN = <FontAwesomeIcon icon={faCaravan} />;
const ICON_BOLT = <FontAwesomeIcon icon={faBolt} />;
const ICON_SEARCH = <FontAwesomeIcon icon={faSearch} />;
const ICON_ANOMALY = <FontAwesomeIcon icon={faExclamationCircle} />;
const ICON_BRIEFCASE = <FontAwesomeIcon icon={faBriefcase} />;
const ICON_GLOBE = <FontAwesomeIcon icon={faGlobe} />;
const ICON_FILE = <FontAwesomeIcon icon={faFileAlt} />;
const ICON_HAIL = <FontAwesomeIcon icon={faCloudShowersHeavy} />;
const ICON_FIRE = <FontAwesomeIcon icon={faFire} />;

export const RIGHT = RIGHTS.reduce((acc, r) => ({ ...acc, [r.key]: r.key }), {});
const KNOWN_RIGHTS = RIGHTS.map((r) => r.key);

// A page is declared ONCE, here.
//   right        the access flag. `displayed[right]` -> the sidebar entry exists;
//                `clickable[right]` -> the page renders (else the ContactRequest teaser).
//   adminOnly    gated by `admin` instead of a flag.
//   superAdmin   the flag still gates companies, but a user with no company (the global
//                super-admin, whose blob has every flag off) always gets the page.
//   teaserTitle  <Nav title> shown on the denied page.
//   contactPage  <ContactRequest page="..."> — the label the sales team receives by
//                email. Copied verbatim from the views; do not "fix" the duplicates.
//   no `name`    -> route only, no sidebar entry.
// Sidebar order = the order below. There are no indices anywhere: entries are pushed
// when allowed, never spliced out afterwards.
const pages = [
  { path: "/", exact: true, layout: DashboardLayout, component: () => <Redirect to="/home" /> },
  { path: "/signin", exact: true, layout: AuthV2Layout, component: Signin },
  { path: "/forgot", exact: true, layout: AuthLayout, component: Forgot },
  { path: "/signout", exact: true, layout: AuthLayout, component: Signout },
  { path: "/settings", exact: true, layout: DashboardLayout, component: Settings },

  {
    icon: ICON_HOME,
    name: "Tableau de bord",
    path: "/home",
    exact: true,
    layout: DashboardLayout,
    component: Home,
  },

  {
    icon: ICON_FILE, name: "Documents", path: "/documents", exact: true,
    layout: DashboardLayout, component: Documents, right: RIGHT.documents, superAdmin: true,
    teaserTitle: "Documents", contactPage: "Documents",
  },
  {
    path: "/documents/:id", exact: true, layout: DashboardLayout, component: DocumentEditor,
    right: RIGHT.documents, superAdmin: true, teaserTitle: "Documents", contactPage: "Documents",
  },

  {
    icon: ICON_ADMIN,
    name: "Administration",
    views: [
      { icon: ICON_COGS, name: "Général", path: "/admin-general", exact: true, layout: DashboardLayout, component: AdminGeneral, adminOnly: true },
      { icon: ICON_USER, name: "Utilisateurs", path: "/admin-user", exact: true, layout: DashboardLayout, component: AdminUser, adminOnly: true },
      { icon: ICON_TOOLS, name: "Réparateurs", path: "/admin-garage", exact: true, layout: DashboardLayout, component: AdminGarage, adminOnly: true },
      { icon: ICON_STORE, name: "Experts", path: "/admin-expert", exact: true, layout: DashboardLayout, component: AdminAgency, adminOnly: true },
    ],
  },

  {
    icon: ICON_CHART_BAR,
    name: "Statistiques",
    views: [
      {
        icon: ICON_TABLE, name: "Spécifiques", path: "/statistics-1", exact: true,
        layout: DashboardLayout, component: StatisticsAbeille, right: RIGHT.statisticsAbeille,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
      {
        icon: ICON_TABLE, name: "Spécifiques", path: "/statistics-2", exact: true,
        layout: DashboardLayout, component: StatisticsAllianz, right: RIGHT.statisticsAllianz,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
      {
        // Its own flag; a blob written before the split inherits statisticsAllianz (rights.json).
        icon: ICON_TABLE, name: "PJ-RC-PM", path: "/statistics-APJ", exact: true,
        layout: DashboardLayout, component: StatisticsAllianzPJ, right: RIGHT.statisticsAllianzPJ,
        teaserTitle: "Statistiques PJ-RC-PM", contactPage: "Statistiques - PJ-RC-PM",
      },
      {
        icon: ICON_TABLE, name: "Spécifiques", path: "/statistics-3", exact: true,
        layout: DashboardLayout, component: StatisticsAxa, right: RIGHT.statisticsAxa,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
      {
        icon: ICON_GLASS, name: "Bris de glace", path: "/broken-glass", exact: true,
        layout: DashboardLayout, component: BrokenGlass, right: RIGHT.glass,
        teaserTitle: "Bris de glace", contactPage: "Statistiques - Bris de glace",
      },
      {
        icon: ICON_CHART_LINE, name: "Selfcare", path: "/selfcare", exact: true,
        layout: DashboardLayout, component: Selfcare, right: RIGHT.selfcare,
        teaserTitle: "Selfcare", contactPage: "Statistiques - Selfcare",
      },
      {
        // The sidebar said "/EGA" while the route said "/ega": the active-entry
        // highlight (an exact === compare) never matched. Unified to "/ega";
        // old /EGA links still resolve (react-router v5 matches case-insensitively).
        icon: ICON_SNOWFLAKE, name: "EGA", path: "/ega", exact: true,
        layout: DashboardLayout, component: Ega, right: RIGHT.ega,
        teaserTitle: "EGA", contactPage: "EGA",
      },
      {
        icon: ICON_CARAVAN, name: "Caravaning", path: "/caravaning", exact: true,
        layout: DashboardLayout, component: Caravaning, right: RIGHT.caravaning,
        teaserTitle: "Caravaning", contactPage: "Caravaning",
      },
      {
        icon: ICON_BOLT, name: "Électrique", path: "/electric", exact: true,
        layout: DashboardLayout, component: Electric, right: RIGHT.electric,
        teaserTitle: "Électrique", contactPage: "Électrique",
      },
      {
        icon: ICON_HAIL, name: "Grêle", path: "/hail", exact: true,
        layout: DashboardLayout, component: Hail, right: RIGHT.hail,
        teaserTitle: "Grêle", contactPage: "Statistiques - Grêle",
      },
      {
        icon: ICON_FIRE, name: "Incendie", path: "/fire", exact: true,
        layout: DashboardLayout, component: Fire, right: RIGHT.fire,
        teaserTitle: "Incendie", contactPage: "Statistiques - Incendie",
      },
      {
        // Same bolt icon as Électrique — as today. Left alone on purpose: this PR
        // changes structure, not what anyone sees.
        icon: ICON_BOLT, name: "Classement", path: "/ranking", exact: true,
        layout: DashboardLayout, component: Ranking, right: RIGHT.ranking,
        teaserTitle: "Classement", contactPage: "Classement",
      },
      {
        icon: ICON_TABLE, name: "Spécifiques", path: "/statistics-4", exact: true,
        layout: DashboardLayout, component: StatisticsCovea, right: RIGHT.statisticsCovea,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
      {
        icon: ICON_TABLE, name: "Générales", path: "/statistics-5", exact: true,
        layout: DashboardLayout, component: StatisticsGeneral, right: RIGHT.statisticsGeneral,
        teaserTitle: "Statistiques générales", contactPage: "Statistiques - Générales",
      },
      {
        icon: ICON_TABLE, name: "Spécifiques", path: "/statistics-AOM", exact: true,
        layout: DashboardLayout, component: StatisticsAllianzOM, right: RIGHT.statisticsAllianzOM,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
      {
        icon: ICON_TOOLS, name: "Suivi réparateurs", path: "/statistics-garage", exact: true,
        layout: DashboardLayout, component: ReparatorPerformance, right: RIGHT.statisticsGarage,
        teaserTitle: "Suivi réparateurs", contactPage: "Statistiques - Suivi réparateurs",
      },
      {
        // Was gated on `statisticsVVR` (Dashboard.js:185) — its own flag now.
        icon: ICON_TOOLS, name: "VVNR", path: "/VVNR", exact: true,
        layout: DashboardLayout, component: VVNR, right: RIGHT.statisticsVVNR,
        teaserTitle: "VVNR", contactPage: "Statistiques - VVNR",
      },
      {
        icon: ICON_TOOLS, name: "VVR", path: "/VVR", exact: true,
        layout: DashboardLayout, component: VVR, right: RIGHT.statisticsVVR,
        teaserTitle: "VVR", contactPage: "Statistiques - VVR",
      },
      {
        icon: ICON_TOOLS, name: "IRSA", path: "/IRSA", exact: true,
        layout: DashboardLayout, component: IRSA, right: RIGHT.statisticsIRSA,
        teaserTitle: "IRSA", contactPage: "Statistiques - IRSA",
      },
      {
        icon: ICON_TOOLS, name: "CarbonCar", path: "/carbon-car", exact: true,
        layout: DashboardLayout, component: ProjectE, right: RIGHT.statisticsCarbonCar,
        teaserTitle: "CarbonCAR", contactPage: "CarbonCAR",
      },
      {
        icon: ICON_TABLE, name: "Statistiques", path: "/statistics-helvetia", exact: true,
        layout: DashboardLayout, component: StatisticsHelvetia, right: RIGHT.statisticsHelvetia,
        teaserTitle: "Statistiques spécifiques", contactPage: "Statistiques - Générales",
      },
    ],
  },

  {
    icon: ICON_SEARCH, name: "Audit", path: "/audit", exact: true,
    layout: DashboardLayout, component: Audit, right: RIGHT.audit,
    teaserTitle: "Audit", contactPage: "Audit",
  },
  {
    icon: ICON_ANOMALY, name: "Anomalie", path: "/anomaly", exact: true,
    layout: DashboardLayout, component: Anomaly, right: RIGHT.anomaly,
    teaserTitle: "Anomalie", contactPage: "Anomalie",
  },

  {
    icon: ICON_BRIEFCASE,
    name: "Répartition missions",
    views: [
      {
        icon: ICON_TABLE, name: "Tableau", path: "/missions-table", exact: true,
        layout: DashboardLayout, component: MissionsTable, right: RIGHT.missions,
        teaserTitle: "Répartition des missions", contactPage: "Répartition missions - Tableau",
      },
      {
        icon: ICON_GLOBE, name: "Carte", path: "/missions-map", exact: true,
        layout: DashboardLayout, component: MissionsMap, right: RIGHT.missions,
        teaserTitle: "Répartition des missions", contactPage: "Répartition missions - Carte",
      },
    ],
  },
];

// A typo'd RIGHT.xxx is `undefined`, and an absent `right` means "always allowed" —
// i.e. a typo would silently make a page public. Fail loudly in dev instead.
if (process.env.NODE_ENV !== "production") {
  const assertPages = (list) =>
    list.forEach((page) => {
      if (page.views) return assertPages(page.views);
      if (!page.path) throw new Error(`[registry] a page has no path: ${page.name}`);
      if (!page.component) throw new Error(`[registry] no component on ${page.path}`);
      if (page.right !== undefined && !KNOWN_RIGHTS.includes(page.right)) {
        throw new Error(`[registry] unknown right on ${page.path}: ${String(page.right)}`);
      }
    });
  assertPages(pages);
}

// Truthy, not `=== true`: identical to today for a blob storing 1/0, and still
// fail-closed on a key the blob does not have at all (undefined -> false).
const hasFlag = (ctx, axis, key) => !!(((ctx.access || {})[axis] || {})[key]);

// Kept identical to the previous checkRights("Administration"): a user with no
// company is the global super-admin. This is a cosmetic check — the server guard
// is `admin === true` alone and must not be widened to match it.
const isAdmin = (ctx) => ctx.admin === true || !ctx.companyId;

const isSuperAdmin = (ctx) => !ctx.companyId;

const isDisplayed = (page, ctx) => {
  if (page.adminOnly) return isAdmin(ctx);
  if (page.superAdmin && isSuperAdmin(ctx)) return true;
  if (!page.right) return true;
  return hasFlag(ctx, "displayed", page.right);
};

const isClickable = (page, ctx) => {
  if (page.adminOnly) return isAdmin(ctx);
  if (page.superAdmin && isSuperAdmin(ctx)) return true;
  if (!page.right) return true;
  return hasFlag(ctx, "clickable", page.right);
};

// `disabled` keeps the greyed-out upsell affordance: the entry stays clickable
// (Sidebar has allowDisabledClick) so the user reaches the ContactRequest form.
// No functions in here — the Sidebar JSON.stringify-diffs this array.
const toSidebarEntry = (page, ctx) => ({
  icon: page.icon,
  name: page.name,
  path: page.path,
  disabled: !isClickable(page, ctx),
});

const toRoute = (page) => ({
  path: page.path,
  exact: page.exact,
  layout: page.layout,
  component: page.component,
  right: page.right || null,
  adminOnly: page.adminOnly === true,
  superAdmin: page.superAdmin === true,
  teaserTitle: page.teaserTitle || page.name || null,
  contactPage: page.contactPage || null,
});

// Returns the sidebar the user may see, and every route (always — a page the user
// cannot open must still be reachable so the teaser can be rendered on it).
const getRoutesSidebar = (ctx) => {
  const sidebar = [];
  const routes = [];

  pages.forEach((page) => {
    if (page.views) {
      page.views.forEach((child) => routes.push(toRoute(child)));

      // A group is visible iff a child survived. Derived, never declared: that is
      // what makes it impossible to forget a flag in a parent's condition.
      const children = page.views.filter((child) => isDisplayed(child, ctx));
      if (children.length) {
        sidebar.push({
          icon: page.icon,
          name: page.name,
          views: children.map((child) => toSidebarEntry(child, ctx)),
        });
      }
      return;
    }

    routes.push(toRoute(page));
    if (page.name && isDisplayed(page, ctx)) sidebar.push(toSidebarEntry(page, ctx));
  });

  routes.push({
    path: "*",
    exact: false,
    layout: DashboardLayout,
    component: Error404,
    right: null,
    adminOnly: false,
    superAdmin: false,
    teaserTitle: null,
    contactPage: null,
  });

  return { sidebar, routes };
};

export default getRoutesSidebar;
